import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Input,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import { format } from 'date-fns';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify from '../../utils/notify';
import { confirmAction, confirmDelete } from '../../utils/confirm';
import '../TenderOpening/components/TenderOpening.css';
import './TenderAnnouncementView.css';
import {
  getTenderAnnouncements,
  getTenderAnnouncementById,
  getAvailableProcurements,
  createTenderAnnouncement,
  updateTenderAnnouncement,
  deleteTenderAnnouncement,
  setTenderAnnouncementPublish,
  uploadTenderAnnouncementDocument,
  updateTenderAnnouncementDocument,
  deleteTenderAnnouncementDocument,
  getTenderAnnouncementDocumentViewUrl,
} from '../../helpers/tenderAnnouncement_helper';
import ActionButton from '../../Components/Common/ActionButton';
import useAsyncAction from '../../Components/Hooks/useAsyncAction';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../Components/Common/ListFilters';
import {
  DateField,
  FormErrorSummary,
  FormField,
  FormGrid,
  RequiredNote,
  SelectField,
  TextAreaField,
  TextField,
  focusFirstError,
  hasErrors,
  required,
  validateForm as runValidation,
} from '../../Components/Form';
import { formatDateTime, formatFileSize } from '../../utils/format';
import StatusBadge from '../../Components/Common/StatusBadge';

// Empat nilai status (D-33). Disimpan PascalCase tanpa spasi; label berspasi
// hanya untuk tampilan — jangan pernah mengirim label ke API.
const STATUS_OPTIONS = ['Draft', 'Announced', 'BiddingOpen', 'BiddingClosed'];

const STATUS_LABEL = {
  Draft: 'Draft',
  Announced: 'Announced',
  BiddingOpen: 'Bidding Open',
  BiddingClosed: 'Bidding Closed',
};

const STATUS_FILTER_OPTIONS = STATUS_OPTIONS.map(value => ({
  value,
  label: STATUS_LABEL[value],
}));

const DATA_SOURCE_FILTER_OPTIONS = [
  { value: 'Procurement', label: 'Plan & Procurement' },
  { value: 'Manual', label: 'Manual Entry' },
];

const PUBLISHED_FILTER_OPTIONS = [
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Not Published' },
];

const CONTRACT_TYPE_OPTIONS = ['Term', 'Spot'].map(c => ({
  value: c,
  label: c,
}));

// Dikunci ke dua nilai (D-22) dan divalidasi ulang di backend. Nilai lama
// 'Hydro & Condensate' sudah dinormalkan lewat migration — jangan dihidupkan lagi.
const PROCUREMENT_CATEGORY_OPTIONS = [
  'Product Hydro',
  'Crude & Condensate',
].map(c => ({ value: c, label: c }));

// Asal data (D-31). Nilai dikirim apa adanya; labelnya urusan tampilan.
const DATA_SOURCE = {
  PROCUREMENT: 'Procurement',
  MANUAL: 'Manual',
};

const DATA_SOURCE_BADGE = {
  [DATA_SOURCE.PROCUREMENT]: {
    label: 'PLAN & PROCUREMENT',
    className: 'ta-badge--procurement',
  },
  [DATA_SOURCE.MANUAL]: {
    label: 'MANUAL ENTRY',
    className: 'ta-badge--manual',
  },
};

// Tiga mode penerbitan (D-39).
const PUBLISH_MODE = {
  UNPUBLISH: 'Unpublish',
  PUBLISH_NOW: 'PublishNow',
  SCHEDULE: 'Schedule',
};

const SUMMARY_CARDS = [
  {
    key: 'total',
    label: 'Total Tender Announcements',
    icon: 'ri-file-list-3-line',
    tone: 'total',
  },
  {
    key: 'published',
    label: 'Published',
    icon: 'ri-megaphone-line',
    tone: 'done',
  },
  {
    key: 'scheduled',
    label: 'Scheduled',
    icon: 'ri-calendar-check-line',
    tone: 'open',
  },
  {
    key: 'unpublished',
    label: 'Unpublished',
    icon: 'ri-draft-line',
    tone: 'muted',
  },
];

const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Sama dengan batas di backend (D-38, §5.9). Diperiksa juga di sini supaya
// admin tidak menunggu permintaan yang sudah pasti ditolak.
const MAX_ATTACHMENTS = 10;
const MAX_ACTION_LINKS = 10;

const EMPTY_FORM = {
  id: null,
  dataSource: DATA_SOURCE.MANUAL,
  procurementId: null,
  isReadOnlyFromProcurement: false,
  announcementNumber: '',
  title: '',
  titleEn: '',
  summary: '',
  summaryEn: '',
  procurementType: '',
  procurementTypeEn: '',
  procurementCategory: '',
  contractType: '',
  proposalSubmissionDeadline: '',
  proposalSubmissionTime: '',
  status: 'Draft',
  awardedToCompanyName: '',
  bids: 0,
  isPublished: false,
  publishStatus: 'Unpublished',
  scheduledPublishDate: '',
  scheduledPublishTime: '',
  actionLinks: [],
};

const JAKARTA_TIME_ZONE = 'Asia/Jakarta';

// Bagian tanggal/jam sebuah instant menurut waktu Jakarta — bukan menurut zona
// perangkat admin, yang bisa berbeda (D-25).
const jakartaParts = value => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: JAKARTA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const lookup = type => parts.find(p => p.type === type)?.value ?? '';
  return {
    year: lookup('year'),
    month: lookup('month'),
    day: lookup('day'),
    hour: lookup('hour'),
    minute: lookup('minute'),
  };
};

const toDateInputValue = value => {
  const parts = jakartaParts(value);
  return parts ? `${parts.year}-${parts.month}-${parts.day}` : '';
};

const toTimeInputValue = value => {
  const parts = jakartaParts(value);
  return parts ? `${parts.hour}:${parts.minute}` : '';
};

// Menggabungkan isian tanggal dan jam menjadi instant berzona WIB. Offset
// ditulis eksplisit supaya nilainya tidak bergeser mengikuti zona perangkat.
const combineJakarta = (dateValue, timeValue) => {
  if (!dateValue) return null;
  const time = timeValue && timeValue.length >= 4 ? timeValue : '00:00';
  return `${dateValue}T${time}:00+07:00`;
};

// DateField hands back a Date; the form keeps the same "yyyy-MM-dd" and
// "HH:mm" strings the API mapping above already uses.
const dateToYmd = date => (date ? format(date, 'yyyy-MM-dd') : '');
const dateToHm = date => (date ? format(date, 'HH:mm') : '');
const hmToDate = value => {
  if (!value) return null;
  const [hour, minute] = String(value).split(':').map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return new Date(2000, 0, 1, hour, minute);
};

const actionLinkFieldName = (index, field) => `actionLinks-${index}-${field}`;

// Response shape from axios is inconsistent in this repo (depends on the
// order the global interceptor in `api_helper.js` is registered) — this
// helper drills down to the `ApiResponse` `data` field, same fallback
// pattern already used by `LawView.js`/`DocumentUploadMasterView.js`.
const extractApiData = response => {
  const result = response?.data ?? response;
  return result?.data ?? result ?? {};
};

const TenderAnnouncementView = () => {
  const [data, setData] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPublished, setFilterPublished] = useState('all');
  const [filterDataSource, setFilterDataSource] = useState('all');

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [modalForm, setModalForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Pemilihan mode saat Create New (D-34): dari procurement, atau manual.
  const [modeModal, setModeModal] = useState(false);
  const [procurementModal, setProcurementModal] = useState(false);
  const [procurementSearch, setProcurementSearch] = useState('');
  const [procurementList, setProcurementList] = useState([]);
  const [procurementLoading, setProcurementLoading] = useState(false);

  // Attachments live inside the same add/edit modal (matches
  // `DocumentUploadMasterView.js`: one form, one Save action). Existing
  // rows (`documentList`) are only present once the announcement has an
  // id; newly picked files are staged client-side and uploaded together
  // with the create/update call when Save is pressed.
  const [documentList, setDocumentList] = useState([]);
  const [stagedFiles, setStagedFiles] = useState([]);

  // Mode penerbitan yang dipilih admin pada form; dikirim setelah Save.
  const [publishMode, setPublishMode] = useState(null);

  document.title = 'Tender Announcement';

  const fetchData = useCallback(async () => {
    const reqId = ++requestRef.current;
    setLoading(true);
    try {
      const params = {
        PageNumber: currentPage,
        PageSize: rowsPerPage,
      };
      if (search) params.Search = search;
      if (filterStatus !== 'all') params.Status = filterStatus;
      if (filterPublished !== 'all')
        params.IsPublished = filterPublished === 'published';
      if (filterDataSource !== 'all') params.DataSource = filterDataSource;

      const response = await getTenderAnnouncements(params);
      if (reqId !== requestRef.current) return; // stale response
      const payload = extractApiData(response);
      const items = payload.items || [];
      setData({ items: Array.isArray(items) ? items : [] });
      setTotalRows(payload.totalItems || items.length || 0);
    } catch (error) {
      if (reqId !== requestRef.current) return;
      notify.apiError(error, 'load data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    search,
    filterStatus,
    filterPublished,
    filterDataSource,
  ]);

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Every filter change goes back to page 1.
  const handleFilterTextChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };
  const handleFilterStatusChange = value => {
    setFilterStatus(value);
    setCurrentPage(1);
  };
  const handleFilterDataSourceChange = value => {
    setFilterDataSource(value);
    setCurrentPage(1);
  };
  const handleFilterPublishedChange = value => {
    setFilterPublished(value);
    setCurrentPage(1);
  };

  const clearFilter = () => {
    setFilterText('');
    setSearch('');
    setFilterStatus('all');
    setFilterPublished('all');
    setFilterDataSource('all');
    setCurrentPage(1);
  };

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterTextChange({ target: { value: '' } }),
    },
    filterStatus !== 'all' && {
      key: 'status',
      label: 'Tender Status',
      value: STATUS_LABEL[filterStatus] || filterStatus,
      onRemove: () => handleFilterStatusChange('all'),
    },
    filterDataSource !== 'all' && {
      key: 'dataSource',
      label: 'Data Source',
      value:
        DATA_SOURCE_FILTER_OPTIONS.find(o => o.value === filterDataSource)
          ?.label ?? filterDataSource,
      onRemove: () => handleFilterDataSourceChange('all'),
    },
    filterPublished !== 'all' && {
      key: 'published',
      label: 'Publish',
      value:
        PUBLISHED_FILTER_OPTIONS.find(o => o.value === filterPublished)
          ?.label ?? filterPublished,
      onRemove: () => handleFilterPublishedChange('all'),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const handlePageChange = page => setCurrentPage(page);
  const handlePerRowsChange = newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  // ------------------------------------------------------------------
  // Create New — pilih mode dulu (D-34)
  // ------------------------------------------------------------------

  const openModeModal = () => setModeModal(true);

  const startManualEntry = () => {
    setModeModal(false);
    setFormData({ ...EMPTY_FORM, dataSource: DATA_SOURCE.MANUAL });
    setErrors({});
    setDocumentList([]);
    setStagedFiles([]);
    setPublishMode(null);
    setModalForm(true);
  };

  const loadAvailableProcurements = useCallback(async search => {
    setProcurementLoading(true);
    try {
      const response = await getAvailableProcurements({
        search: search || undefined,
        pageNumber: 1,
        pageSize: 20,
      });
      const payload = extractApiData(response);
      setProcurementList(payload.items || []);
    } catch (error) {
      notify.apiError(error, 'load procurements');
      setProcurementList([]);
    } finally {
      setProcurementLoading(false);
    }
  }, []);

  const startFromProcurement = () => {
    setModeModal(false);
    setProcurementSearch('');
    setProcurementModal(true);
    loadAvailableProcurements('');
  };

  // Field turunan procurement diisi backend saat create — di sini hanya nilai
  // pratinjau supaya admin melihat apa yang akan tersimpan.
  const selectProcurement = procurement => {
    setProcurementModal(false);
    setFormData({
      ...EMPTY_FORM,
      dataSource: DATA_SOURCE.PROCUREMENT,
      procurementId: procurement.id,
      isReadOnlyFromProcurement: true,
      announcementNumber: procurement.procurementNumber || '',
      procurementCategory: procurement.procurementCategory || '',
      proposalSubmissionDeadline: toDateInputValue(
        procurement.proposalSubmissionDeadline
      ),
      proposalSubmissionTime: toTimeInputValue(
        procurement.proposalSubmissionDeadline
      ),
      title: procurement.titleProcurement || '',
      titleEn: procurement.titleProcurement || '',
    });
    setErrors({});
    setDocumentList([]);
    setStagedFiles([]);
    setPublishMode(null);
    setModalForm(true);
  };

  const openEditModal = async row => {
    try {
      const response = await getTenderAnnouncementById(row.id);
      const item = extractApiData(response);
      setFormData({
        id: item.id,
        dataSource: item.dataSource || DATA_SOURCE.MANUAL,
        procurementId: item.procurementId || null,
        isReadOnlyFromProcurement: Boolean(item.isReadOnlyFromProcurement),
        announcementNumber: item.announcementNumber || '',
        title: item.title || '',
        titleEn: item.titleEn || '',
        summary: item.summary || '',
        summaryEn: item.summaryEn || '',
        procurementType: item.procurementType || '',
        procurementTypeEn: item.procurementTypeEn || '',
        procurementCategory: item.procurementCategory || '',
        contractType: item.contractType || '',
        proposalSubmissionDeadline: toDateInputValue(
          item.proposalSubmissionDeadline
        ),
        proposalSubmissionTime: toTimeInputValue(
          item.proposalSubmissionDeadline
        ),
        status: item.status || 'Draft',
        awardedToCompanyName: item.awardedToCompanyName || '',
        bids: item.bids ?? 0,
        isPublished: Boolean(item.isPublished),
        publishStatus: item.publishStatus || 'Unpublished',
        scheduledPublishDate: toDateInputValue(item.scheduledPublishDate),
        scheduledPublishTime: toTimeInputValue(item.scheduledPublishDate),
        actionLinks: (item.actionLinks || []).map(link => ({
          description: link.description || '',
          buttonLabel: link.buttonLabel || '',
          targetUrl: link.targetUrl || '',
          displayOrder: link.displayOrder ?? 0,
        })),
      });
      setErrors({});
      setDocumentList(item.documents || []);
      setStagedFiles([]);
      setPublishMode(null);
      setModalForm(true);
    } catch (error) {
      notify.apiError(error, 'load detail');
    }
  };

  // Editing a field clears its own error straight away.
  const clearError = name =>
    setErrors(prev => (prev[name] ? { ...prev, [name]: '' } : prev));

  const setField = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleFormChange = e => setField(e.target.name, e.target.value);

  const isReadOnly = formData.isReadOnlyFromProcurement;
  const lockMode = isReadOnly ? 'readonly' : 'editable';

  // ------------------------------------------------------------------
  // Action Link (D-37)
  // ------------------------------------------------------------------

  const addActionLink = () => {
    if (formData.actionLinks.length >= MAX_ACTION_LINKS) {
      notify.warning(
        `Maximum ${MAX_ACTION_LINKS} action links per announcement.`
      );
      return;
    }
    setFormData(prev => ({
      ...prev,
      actionLinks: [
        ...prev.actionLinks,
        {
          description: '',
          buttonLabel: '',
          targetUrl: '',
          displayOrder: prev.actionLinks.length,
        },
      ],
    }));
  };

  const updateActionLink = (index, field, value) => {
    clearError(actionLinkFieldName(index, field));
    setFormData(prev => ({
      ...prev,
      actionLinks: prev.actionLinks.map((link, i) =>
        i === index ? { ...link, [field]: value } : link
      ),
    }));
  };

  const removeActionLink = index => {
    // Link errors are keyed by position, so they would point at the wrong
    // link once one is removed.
    setErrors(prev =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => !key.startsWith('actionLinks-'))
      )
    );
    setFormData(prev => ({
      ...prev,
      actionLinks: prev.actionLinks
        .filter((_, i) => i !== index)
        .map((link, i) => ({ ...link, displayOrder: i })),
    }));
  };

  const buildPayload = () => {
    const payload = {
      title: formData.title,
      titleEn: formData.titleEn || null,
      summary: formData.summary || null,
      summaryEn: formData.summaryEn || null,
      awardedToCompanyName: formData.awardedToCompanyName || null,
      actionLinks: formData.actionLinks.map((link, index) => ({
        description: link.description,
        buttonLabel: link.buttonLabel,
        targetUrl: link.targetUrl,
        displayOrder: index,
      })),
    };

    // Field turunan procurement sengaja tidak dikirim untuk baris Procurement —
    // backend mengabaikannya (§4), jadi mengirimnya hanya menyesatkan pembaca.
    if (!isReadOnly) {
      payload.announcementNumber = formData.announcementNumber;
      payload.procurementType = formData.procurementType || null;
      payload.procurementTypeEn = formData.procurementTypeEn || null;
      payload.procurementCategory = formData.procurementCategory || null;
      payload.contractType = formData.contractType || null;
      payload.proposalSubmissionDeadline = combineJakarta(
        formData.proposalSubmissionDeadline,
        formData.proposalSubmissionTime
      );
      payload.status = formData.status;
      payload.bids = Number(formData.bids) || 0;
    }

    if (!formData.id) {
      payload.dataSource = formData.dataSource;
      payload.procurementId =
        formData.dataSource === DATA_SOURCE.PROCUREMENT
          ? formData.procurementId
          : null;
    }

    return payload;
  };

  const buildSchema = () => {
    const schema = {};
    if (!isReadOnly) {
      schema.announcementNumber = {
        label: 'Announcement number',
        rules: [required()],
      };
      schema.status = { label: 'Status', rules: [required()] };
      schema.bids = {
        label: 'Bids',
        rules: [
          value => (Number(value) < 0 ? 'Bids cannot be negative' : undefined),
        ],
      };
    }
    schema.title = { label: 'Title (Indonesian)', rules: [required()] };
    formData.actionLinks.forEach((_, index) => {
      schema[actionLinkFieldName(index, 'description')] = {
        label: 'Description',
        rules: [required()],
      };
      schema[actionLinkFieldName(index, 'buttonLabel')] = {
        label: 'Button label',
        rules: [required()],
      };
      schema[actionLinkFieldName(index, 'targetUrl')] = {
        label: 'Target URL',
        rules: [
          required(),
          value =>
            /^https?:\/\//i.test(String(value).trim())
              ? undefined
              : 'Target URL must start with http:// or https://',
        ],
      };
    });
    if (publishMode === PUBLISH_MODE.SCHEDULE) {
      schema.scheduledPublishDate = {
        label: 'Scheduled date',
        rules: [
          required(),
          (value, { values }) => {
            const scheduled = new Date(
              combineJakarta(value, values.scheduledPublishTime)
            );
            return scheduled.getTime() <= Date.now()
              ? 'Scheduled date and time must be in the future'
              : undefined;
          },
        ],
      };
    }
    return schema;
  };

  // Labels for the error summary; action link fields say which link.
  const errorSummaryLabels = () => {
    const labels = {};
    Object.entries(buildSchema()).forEach(([name, { label }]) => {
      const match = name.match(/^actionLinks-(\d+)-/);
      labels[name] = match
        ? `Action link ${Number(match[1]) + 1} ${label.toLowerCase()}`
        : label;
    });
    return labels;
  };

  const validateForm = () => {
    const values = { ...formData };
    formData.actionLinks.forEach((link, index) => {
      ['description', 'buttonLabel', 'targetUrl'].forEach(field => {
        values[actionLinkFieldName(index, field)] = link[field];
      });
    });
    const schema = buildSchema();
    const nextErrors = runValidation(values, schema);
    setErrors(nextErrors);
    if (!hasErrors(nextErrors)) return true;
    // Screen order, so the first error on screen gets the focus.
    focusFirstError(nextErrors, [
      'announcementNumber',
      'status',
      'bids',
      'title',
      ...Object.keys(schema).filter(name => name.startsWith('actionLinks-')),
      'scheduledPublishDate',
    ]);
    return false;
  };

  const handleAddStagedFiles = e => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    const accepted = [];
    for (const file of files) {
      if (file.type !== 'application/pdf') {
        notify.warning(
          `"${file.name}" was skipped — only PDF files are allowed.`
        );
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        notify.warning(
          `"${file.name}" was skipped — maximum file size is 10MB.`
        );
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;

    // Batas dihitung dari lampiran tersimpan DITAMBAH yang masih menunggu —
    // kalau tidak, admin baru tahu batasnya setelah separuh berkas terkirim.
    const room = MAX_ATTACHMENTS - documentList.length - stagedFiles.length;
    if (room <= 0) {
      notify.warning(
        `Maximum ${MAX_ATTACHMENTS} attachments per announcement.`
      );
      return;
    }
    if (accepted.length > room) {
      notify.warning(
        `Only ${room} more attachment(s) can be added — maximum is ${MAX_ATTACHMENTS}.`
      );
    }

    setStagedFiles(prev => [...prev, ...accepted.slice(0, room)]);
  };

  const removeStagedFile = index => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Create/update the record, then upload any newly staged files — one
  // Save action covers both, same as `DocumentUploadMasterView.js`.
  const handleSaveForm = async () => {
    if (!validateForm()) return;

    // Going live (or coming down) changes what the public sees right away.
    if (
      publishMode === PUBLISH_MODE.PUBLISH_NOW &&
      formData.publishStatus !== 'Published'
    ) {
      const confirmed = await confirmAction({
        tone: 'primary',
        title: 'Publish this announcement now?',
        message:
          'It becomes visible on the public tender announcement page as soon as you save. You can unpublish it later.',
        subject: formData.title,
        confirmLabel: 'Publish announcement',
      });
      if (!confirmed) return;
    } else if (
      publishMode === PUBLISH_MODE.UNPUBLISH &&
      (formData.publishStatus === 'Published' ||
        formData.publishStatus === 'Scheduled')
    ) {
      const confirmed = await confirmAction({
        tone: 'warning',
        title: 'Unpublish this announcement?',
        message:
          formData.publishStatus === 'Scheduled'
            ? 'The scheduled publication is cancelled and the announcement stays hidden from the public page.'
            : 'It is taken off the public tender announcement page as soon as you save.',
        subject: formData.title,
        confirmLabel: 'Unpublish announcement',
      });
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      let announcementId = formData.id;

      if (announcementId) {
        await updateTenderAnnouncement(announcementId, payload);
      } else {
        const createResponse = await createTenderAnnouncement(payload);
        announcementId = extractApiData(createResponse).id;
      }

      // Berkas yang gagal tetap ditahan supaya admin bisa mencoba lagi tanpa
      // memilih ulang; namanya disebut supaya jelas mana yang bermasalah.
      const failedFiles = [];
      for (const file of stagedFiles) {
        try {
          await uploadTenderAnnouncementDocument(announcementId, file);
        } catch (error) {
          failedFiles.push(file);
        }
      }

      if (publishMode) {
        try {
          await setTenderAnnouncementPublish(
            announcementId,
            publishMode,
            publishMode === PUBLISH_MODE.SCHEDULE
              ? combineJakarta(
                  formData.scheduledPublishDate,
                  formData.scheduledPublishTime
                )
              : null
          );
        } catch (error) {
          notify.apiError(error, 'change publish status');
        }
      }

      if (failedFiles.length > 0) {
        setStagedFiles(failedFiles);
        notify.error(
          `Saved, but these attachments failed to upload: ${failedFiles
            .map(f => f.name)
            .join(', ')}`
        );
      } else {
        notify.success(
          formData.id
            ? 'Tender Announcement updated successfully.'
            : 'Tender Announcement added successfully.'
        );
        setModalForm(false);
        setStagedFiles([]);
      }

      fetchData();
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = row => {
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return;
    try {
      await deleteTenderAnnouncement(selectedRow.id);
      notify.success('Tender Announcement deleted successfully.');
      setDeleteModal(false);
      setSelectedRow(null);
      fetchData();
    } catch (error) {
      notify.apiError(error, 'delete the announcement');
    }
  };

  // Existing attachments (row already has an id) are managed immediately —
  // no reason to defer a toggle/delete on an already-saved file until Save
  // is pressed again.
  const reloadExistingAttachments = async () => {
    if (!formData.id) return;
    try {
      const response = await getTenderAnnouncementById(formData.id);
      const item = extractApiData(response);
      setDocumentList(item.documents || []);
    } catch (error) {
      notify.apiError(error, 'load attachments');
    }
  };

  const handleViewDocument = async documentId => {
    try {
      const response = await getTenderAnnouncementDocumentViewUrl(documentId);
      const { url } = extractApiData(response);
      if (!url) {
        notify.error('Failed to open attachment. Please try again.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      notify.apiError(error, 'open attachment');
    }
  };

  const handleToggleShowToPublic = async doc => {
    try {
      await updateTenderAnnouncementDocument(doc.id, {
        isShowToPublic: !doc.isShowToPublic,
      });
      await reloadExistingAttachments();
    } catch (error) {
      notify.apiError(error, 'update attachment');
    }
  };
  const [runToggleShowToPublic, togglingShowToPublic] = useAsyncAction(
    handleToggleShowToPublic
  );

  // Menukar displayOrder dua lampiran bertetangga. Urutan ini yang dipakai
  // halaman publik, jadi perubahannya langsung dikirim, bukan ditunda ke Save.
  const moveAttachment = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= documentList.length) return;

    const current = documentList[index];
    const neighbour = documentList[target];

    try {
      await updateTenderAnnouncementDocument(current.id, {
        displayOrder: neighbour.displayOrder,
      });
      await updateTenderAnnouncementDocument(neighbour.id, {
        displayOrder: current.displayOrder,
      });
      await reloadExistingAttachments();
    } catch (error) {
      notify.apiError(error, 'reorder attachment');
    }
  };
  const [runMoveAttachment, movingAttachment] = useAsyncAction(moveAttachment);

  const handleDeleteDocument = async doc => {
    if (!(await confirmDelete('attachment', doc.fileName))) return;
    try {
      await deleteTenderAnnouncementDocument(doc.id);
      notify.success('Attachment deleted successfully.');
      reloadExistingAttachments();
    } catch (error) {
      notify.apiError(error, 'delete attachment');
    }
  };

  const statusBadgeColor = status => {
    switch (status) {
      case 'BiddingOpen':
        return 'success';
      case 'BiddingClosed':
        return 'danger';
      case 'Announced':
        return 'info';
      default:
        return 'muted';
    }
  };

  const publishBadgeColor = publishStatus => {
    switch (publishStatus) {
      case 'Published':
        return 'success';
      case 'Scheduled':
        return 'info';
      default:
        return 'muted';
    }
  };

  // Dihitung dari `totalRows` (seluruh baris hidup menurut server) dan dari
  // halaman yang sedang tampil untuk pecahan statusnya.
  const summaryCounts = {
    total: totalRows,
    published: (data.items || []).filter(i => i.publishStatus === 'Published')
      .length,
    scheduled: (data.items || []).filter(i => i.publishStatus === 'Scheduled')
      .length,
    unpublished: (data.items || []).filter(
      i => i.publishStatus === 'Unpublished'
    ).length,
  };

  // Delapan kolom (D-42). Lebarnya tetap dan tabelnya menggulung horizontal —
  // memaksa semuanya muat membuat Tender Info tidak terbaca.
  const columns = [
    {
      name: 'Created Date',
      selector: row => row.createdDate,
      sortable: true,
      width: '11rem',
      cell: row => (
        <span className='to-date'>{formatDateTime(row.createdDate)}</span>
      ),
    },
    {
      name: 'Updated Date',
      selector: row => row.updatedDate,
      sortable: true,
      width: '11rem',
      cell: row => (
        <span className='to-date'>
          {row.updatedDate ? formatDateTime(row.updatedDate) : '—'}
        </span>
      ),
    },
    {
      name: 'Tender Info',
      selector: row => row.title,
      sortable: true,
      minWidth: '20rem',
      grow: 2,
      wrap: true,
      cell: row => (
        <div className='py-1'>
          <span className='to-title' title={String(row.title ?? '')}>
            {row.title}
          </span>
          <div className='to-product'>{row.announcementNumber}</div>
        </div>
      ),
    },
    {
      name: 'Data Source',
      selector: row => row.dataSource,
      sortable: true,
      width: '11.5rem',
      cell: row => {
        const badge = DATA_SOURCE_BADGE[row.dataSource];
        if (!badge) return <span className='text-muted'>—</span>;
        return (
          <span className={`ta-badge ${badge.className}`}>{badge.label}</span>
        );
      },
    },
    {
      name: 'Type',
      selector: row => row.procurementCategory,
      sortable: true,
      width: '10rem',
      wrap: true,
      cell: row =>
        row.procurementCategory ? (
          <span className='to-tag'>{row.procurementCategory}</span>
        ) : (
          <span className='text-muted'>—</span>
        ),
    },
    {
      name: 'Status Tender',
      selector: row => row.status,
      sortable: true,
      width: '9.5rem',
      cell: row => (
        <StatusBadge
          tone={statusBadgeColor(row.status)}
          label={STATUS_LABEL[row.status] || row.status}
        />
      ),
    },
    {
      name: 'Status Publish',
      selector: row => row.publishStatus,
      sortable: true,
      width: '9.5rem',
      cell: row => (
        <StatusBadge
          tone={publishBadgeColor(row.publishStatus)}
          label={row.publishStatus}
        />
      ),
    },
    {
      name: 'Actions',
      width: '7rem',
      center: true,
      sortable: false,
      // Terbitkan dan Lampiran sengaja TIDAK ada di sini — keduanya ada di
      // dalam form ubah (D-29). Baris cukup ubah dan hapus.
      cell: row => (
        <div className='d-flex gap-1 justify-content-end'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => openEditModal(row)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteClick(row)}
          />
        </div>
      ),
    },
  ];

  // Publikasi tidak boleh dilakukan selama status masih Draft (D-45): barisnya
  // tidak akan muncul di halaman publik walaupun IsPublished tersimpan true.
  const isDraftStatus = formData.status === 'Draft';
  const isClosedStatus = formData.status === 'BiddingClosed';
  const hasPendingSchedule = formData.publishStatus === 'Scheduled';

  const middleCardLabel = formData.isPublished
    ? 'Currently Published'
    : hasPendingSchedule
      ? 'Force Publish Now'
      : 'Publish Immediately';

  const middleCardDesc = formData.isPublished
    ? 'This announcement is currently live and visible to the public.'
    : hasPendingSchedule
      ? 'Override schedule and go live instantly.'
      : 'Go live instantly. The announcement date will record the current time.';

  const selectedPublishCard =
    publishMode ??
    (formData.publishStatus === 'Published'
      ? PUBLISH_MODE.PUBLISH_NOW
      : formData.publishStatus === 'Scheduled'
        ? PUBLISH_MODE.SCHEDULE
        : PUBLISH_MODE.UNPUBLISH);

  const renderPublishCard = (mode, icon, title, desc, disabled) => (
    <Col md={4}>
      <button
        type='button'
        disabled={disabled}
        onClick={() => setPublishMode(mode)}
        className={`ta-publish-card ${
          selectedPublishCard === mode ? 'is-selected' : ''
        } ${disabled ? 'is-disabled' : ''}`}
      >
        <i className={`${icon} ta-publish-card__icon`} aria-hidden='true' />
        <span className='ta-publish-card__title'>{title}</span>
        <span className='ta-publish-card__desc'>{desc}</span>
        {selectedPublishCard === mode && !disabled && (
          <i className='ri-checkbox-circle-line ta-publish-card__check' />
        )}
      </button>
    </Col>
  );

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteConfirm}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this tender announcement?'
          message='The announcement will be permanently deleted. This cannot be undone.'
          subject={selectedRow?.title || selectedRow?.announcementNumber}
          confirmLabel='Delete announcement'
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{
                      label: 'Tender Announcement',
                      to: '/apps-tender-announcement/view',
                    }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-megaphone-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Tender Announcement</h4>
                      <p className='to-hero-subtitle'>
                        Manage and publish tender announcements.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() => fetchData()}
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={openModeModal}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New
                      </button>
                    </div>
                  </div>
                </div>

                {/* Empat kartu ringkasan. Angka dihitung dari data halaman yang
                    sedang tampil — sengaja TIDAK membuat endpoint statistik baru
                    (di luar cakupan). */}
                <Row className='g-3 mb-4'>
                  {SUMMARY_CARDS.map(card => (
                    <Col xl={3} sm={6} key={card.key}>
                      <div className={`to-stat to-stat--${card.tone}`}>
                        <span className='to-stat-icon'>
                          <i className={card.icon} aria-hidden='true' />
                        </span>
                        <span>
                          <span className='to-stat-label'>{card.label}</span>
                          <span className='to-stat-value'>
                            {summaryCounts[card.key]}
                          </span>
                        </span>
                      </div>
                    </Col>
                  ))}
                </Row>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterTextChange}
                      placeholder='Search announcements...'
                      ariaLabel='Search announcements'
                    />
                    <ListFilterSelect
                      label='Tender Status'
                      value={filterStatus}
                      onChange={handleFilterStatusChange}
                      allLabel='All Statuses'
                      emptyValue='all'
                      options={STATUS_FILTER_OPTIONS}
                    />
                    <ListFilterSelect
                      label='Data Source'
                      value={filterDataSource}
                      onChange={handleFilterDataSourceChange}
                      allLabel='All Data Sources'
                      emptyValue='all'
                      options={DATA_SOURCE_FILTER_OPTIONS}
                    />
                    <ListFilterSelect
                      label='Publish'
                      value={filterPublished}
                      onChange={handleFilterPublishedChange}
                      allLabel='All (Draft / Published)'
                      emptyValue='all'
                      options={PUBLISHED_FILTER_OPTIONS}
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} announcements`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No tender announcement found'
                          emptyHint='No tender announcements have been created or published yet.'
                          filteredHint='Nothing matches the current search or filters.'
                          hasFilters={hasFilters}
                          onClear={clearFilter}
                          action={{
                            label: 'Create New',
                            onClick: openModeModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Pilihan mode saat Create New (D-34). */}
          <Modal
            isOpen={modeModal}
            centered
            toggle={() => setModeModal(!modeModal)}
          >
            <ModalHeader toggle={() => setModeModal(!modeModal)}>
              <span className='modal-title fw-bold'>
                New Tender Announcement
              </span>
            </ModalHeader>
            <ModalBody>
              <p className='text-muted'>
                Choose where the announcement data comes from. This cannot be
                changed later.
              </p>
              <Row className='g-3'>
                <Col md={6}>
                  <button
                    type='button'
                    className='ta-mode-card'
                    onClick={startFromProcurement}
                  >
                    <i className='ri-database-2-line ta-mode-card__icon' />
                    <span className='ta-mode-card__title'>
                      From Procurement
                    </span>
                    <span className='ta-mode-card__desc'>
                      Pick an existing procurement. Most fields stay in sync and
                      cannot be edited here.
                    </span>
                  </button>
                </Col>
                <Col md={6}>
                  <button
                    type='button'
                    className='ta-mode-card'
                    onClick={startManualEntry}
                  >
                    <i className='ri-edit-2-line ta-mode-card__icon' />
                    <span className='ta-mode-card__title'>Manual Entry</span>
                    <span className='ta-mode-card__desc'>
                      Type everything by hand. Not linked to any procurement.
                    </span>
                  </button>
                </Col>
              </Row>
            </ModalBody>
          </Modal>

          {/* Pencarian procurement yang belum punya pengumuman. */}
          <Modal
            isOpen={procurementModal}
            centered
            size='lg'
            toggle={() => setProcurementModal(!procurementModal)}
          >
            <ModalHeader toggle={() => setProcurementModal(!procurementModal)}>
              <span className='modal-title fw-bold'>Select Procurement</span>
            </ModalHeader>
            <ModalBody>
              <div className='d-flex gap-2 mb-3'>
                <Input
                  type='text'
                  placeholder='Search number / title...'
                  value={procurementSearch}
                  onChange={e => setProcurementSearch(e.target.value)}
                  onKeyDown={e =>
                    e.key === 'Enter' &&
                    !procurementLoading &&
                    loadAvailableProcurements(procurementSearch)
                  }
                />
                <ActionButton
                  variant='primary'
                  icon='ri-search-line'
                  onClick={() => loadAvailableProcurements(procurementSearch)}
                >
                  Search
                </ActionButton>
              </div>

              {procurementLoading && <SkeletonRows count={4} />}

              {!procurementLoading && procurementList.length === 0 && (
                <p className='text-muted mb-0'>
                  No procurement available. Every submitted procurement already
                  has an announcement.
                </p>
              )}

              {!procurementLoading &&
                procurementList.map(item => (
                  <button
                    type='button'
                    key={item.id}
                    className='ta-procurement-row flex-wrap'
                    onClick={() => selectProcurement(item)}
                  >
                    <div className='flex-grow-1 text-start'>
                      <div className='fw-semibold'>{item.titleProcurement}</div>
                      <div className='text-muted fs-12'>
                        {item.procurementNumber}
                        {item.procurementCategory
                          ? ` · ${item.procurementCategory}`
                          : ''}
                      </div>
                    </div>
                    <span className='text-muted fs-12 text-nowrap'>
                      {item.proposalSubmissionDeadline
                        ? formatDateTime(item.proposalSubmissionDeadline)
                        : '—'}
                    </span>
                  </button>
                ))}
            </ModalBody>
          </Modal>

          {/* Add/edit modal — general data + bilingual text + attachments +
              action links + publication, all saved in one action. */}
          <Modal
            isOpen={modalForm}
            role='dialog'
            autoFocus
            centered
            id='tenderAnnouncementFormModal'
            toggle={() => setModalForm(!modalForm)}
            data-bs-backdrop='static'
            size='xl'
            className='tender-announcement-modal'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalForm(!modalForm)}
            >
              <span className='modal-title text-info fw-bold me-2'>
                {formData.id
                  ? 'Edit Tender Announcement'
                  : 'Add Tender Announcement'}
              </span>
              {DATA_SOURCE_BADGE[formData.dataSource] && (
                <span
                  className={`ta-badge ${DATA_SOURCE_BADGE[formData.dataSource].className}`}
                >
                  {DATA_SOURCE_BADGE[formData.dataSource].label}
                </span>
              )}
            </ModalHeader>
            <ModalBody>
              <RequiredNote />
              <FormErrorSummary errors={errors} labels={errorSummaryLabels()} />

              <Card className='ta-section'>
                <CardBody>
                  <h6 className='ta-section__title'>
                    <i className='ri-list-check-2 me-2' />
                    Announcement
                  </h6>

                  {isReadOnly && (
                    <p className='ta-hint'>
                      Fields with a lock follow the Procurement data and cannot
                      be changed here.
                    </p>
                  )}

                  <FormGrid columns={2}>
                    <TextField
                      label='Announcement number'
                      name='announcementNumber'
                      required
                      placeholder='e.g. TA-2026-001'
                      mode={lockMode}
                      value={formData.announcementNumber}
                      error={errors.announcementNumber}
                      onChange={handleFormChange}
                    />
                    <SelectField
                      label='Tender status'
                      name='status'
                      required
                      mode={lockMode}
                      options={STATUS_OPTIONS.map(value => ({
                        value,
                        label: STATUS_LABEL[value],
                      }))}
                      value={formData.status}
                      error={errors.status}
                      onChange={value => setField('status', value ?? '')}
                    />
                    <SelectField
                      label='Contract type'
                      name='contractType'
                      mode={lockMode}
                      options={CONTRACT_TYPE_OPTIONS}
                      value={formData.contractType}
                      onChange={value => setField('contractType', value ?? '')}
                    />
                    <SelectField
                      label='Procurement category'
                      name='procurementCategory'
                      mode={lockMode}
                      options={PROCUREMENT_CATEGORY_OPTIONS}
                      value={formData.procurementCategory}
                      onChange={value =>
                        setField('procurementCategory', value ?? '')
                      }
                    />
                    <TextField
                      label='Procurement method (Indonesian)'
                      name='procurementType'
                      mode={lockMode}
                      value={formData.procurementType}
                      onChange={handleFormChange}
                    />
                    {/* Sengaja tidak read-only walau baris berasal dari procurement:
                        procurement hanya menyimpan satu bahasa, jadi terjemahan
                        Inggrisnya milik admin dan tidak pernah ditimpa sinkronisasi. */}
                    <TextField
                      label='Procurement method (English)'
                      name='procurementTypeEn'
                      value={formData.procurementTypeEn}
                      onChange={handleFormChange}
                    />
                  </FormGrid>
                </CardBody>
              </Card>

              <Card className='ta-section'>
                <CardBody>
                  <h6 className='ta-section__title'>
                    <i className='ri-calendar-event-line me-2' />
                    Deadline and result
                  </h6>

                  <FormGrid columns={2}>
                    <DateField
                      label='Proposal submission date'
                      name='proposalSubmissionDeadline'
                      hint='Jakarta local time (WIB)'
                      mode={lockMode}
                      value={formData.proposalSubmissionDeadline}
                      onChange={date =>
                        setField('proposalSubmissionDeadline', dateToYmd(date))
                      }
                    />
                    <DateField
                      label='Proposal submission time (WIB)'
                      name='proposalSubmissionTime'
                      picker='time'
                      mode={lockMode}
                      value={hmToDate(formData.proposalSubmissionTime)}
                      onChange={date =>
                        setField('proposalSubmissionTime', dateToHm(date))
                      }
                    />
                    <TextField
                      label='Bids'
                      name='bids'
                      type='number'
                      min='0'
                      mode={lockMode}
                      value={formData.bids}
                      error={errors.bids}
                      onChange={handleFormChange}
                    />
                    <TextField
                      label='Awarded to'
                      name='awardedToCompanyName'
                      placeholder='e.g. PT Example Energi'
                      mode={lockMode}
                      value={formData.awardedToCompanyName}
                      onChange={handleFormChange}
                    />
                  </FormGrid>
                </CardBody>
              </Card>

              <Card className='ta-section'>
                <CardBody>
                  <h6 className='ta-section__title'>
                    <i className='ri-file-text-line me-2' />
                    Detail tender information
                  </h6>
                  <p className='ta-hint'>
                    If the English column is left empty, the Indonesian text
                    will be shown to the public.
                  </p>

                  <FormGrid columns={2}>
                    <TextField
                      label='Title (Indonesian)'
                      name='title'
                      required
                      value={formData.title}
                      error={errors.title}
                      onChange={handleFormChange}
                    />
                    <TextField
                      label='Title (English)'
                      name='titleEn'
                      value={formData.titleEn}
                      onChange={handleFormChange}
                    />
                    <TextAreaField
                      label='Summary (Indonesian)'
                      name='summary'
                      rows={5}
                      value={formData.summary}
                      onChange={handleFormChange}
                    />
                    <TextAreaField
                      label='Summary (English)'
                      name='summaryEn'
                      rows={5}
                      value={formData.summaryEn}
                      onChange={handleFormChange}
                    />
                  </FormGrid>
                </CardBody>
              </Card>

              <Card className='ta-section'>
                <CardBody>
                  <div className='d-flex flex-wrap gap-2 align-items-center mb-2'>
                    <h6 className='ta-section__title mb-0 flex-grow-1'>
                      <i className='ri-attachment-2 me-2' />
                      Attachments
                    </h6>
                  </div>

                  {!formData.id && (
                    <p className='text-muted small mb-2'>
                      Files picked here are uploaded once the announcement is
                      saved.
                    </p>
                  )}

                  {documentList.length === 0 && stagedFiles.length === 0 && (
                    <p className='text-muted small mb-2'>No attachments yet.</p>
                  )}

                  {documentList.map((doc, index) => (
                    <div
                      key={doc.id}
                      className='d-flex align-items-center gap-2 mb-2 p-2 border rounded'
                    >
                      <span className='ta-attachment-order'>{index + 1}</span>
                      <i className='ri-file-pdf-2-line fs-16 text-danger'></i>
                      <div className='flex-grow-1 min-w-0'>
                        <div className='small fw-semibold text-truncate'>
                          {doc.fileName}
                        </div>
                        <div
                          className='text-muted'
                          style={{ fontSize: '0.75rem' }}
                        >
                          {formatFileSize(doc.fileSizeBytes)}
                        </div>
                      </div>

                      <ActionButton
                        variant='icon'
                        size='sm'
                        icon='ri-arrow-up-line'
                        label='Move up'
                        disabled={index === 0 || movingAttachment}
                        onClick={() => runMoveAttachment(index, -1)}
                      />
                      <ActionButton
                        variant='icon'
                        size='sm'
                        icon='ri-arrow-down-line'
                        label='Move down'
                        disabled={
                          index === documentList.length - 1 || movingAttachment
                        }
                        onClick={() => runMoveAttachment(index, 1)}
                      />
                      <ActionButton
                        variant='icon'
                        size='sm'
                        icon='ri-eye-line'
                        label='View'
                        onClick={() => handleViewDocument(doc.id)}
                      />
                      <div
                        className='form-check form-switch me-2'
                        title='Show to public'
                      >
                        <Input
                          type='checkbox'
                          role='switch'
                          className='form-check-input'
                          aria-label='Show to public'
                          checked={doc.isShowToPublic}
                          disabled={togglingShowToPublic}
                          onChange={() => runToggleShowToPublic(doc)}
                        />
                      </div>
                      <ActionButton
                        variant='icon'
                        size='sm'
                        danger
                        icon='ri-delete-bin-line'
                        label='Delete'
                        confirm={false}
                        onClick={() => handleDeleteDocument(doc)}
                      />
                    </div>
                  ))}

                  {stagedFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className='d-flex align-items-center gap-2 mb-2 p-2 border border-dashed rounded'
                    >
                      <i className='ri-file-pdf-2-line fs-16 text-muted'></i>
                      <div className='flex-grow-1 min-w-0'>
                        <div className='small fw-semibold text-truncate'>
                          {file.name}
                        </div>
                        <div
                          className='text-muted'
                          style={{ fontSize: '0.75rem' }}
                        >
                          {formatFileSize(file.size)} — will be uploaded on Save
                        </div>
                      </div>
                      <ActionButton
                        variant='icon'
                        size='sm'
                        danger
                        icon='ri-close-line'
                        label='Remove file'
                        confirm={{
                          title: 'Remove file?',
                          message: 'This file will not be uploaded.',
                          confirmLabel: 'Remove file',
                        }}
                        onClick={() => removeStagedFile(index)}
                      />
                    </div>
                  ))}

                  <FormField
                    label='Add files'
                    name='attachments'
                    hint={`PDF only, max 10MB per file. You can attach multiple files (maximum ${MAX_ATTACHMENTS}).`}
                  >
                    {control => (
                      <Input
                        {...control}
                        type='file'
                        accept='application/pdf'
                        multiple
                        onChange={handleAddStagedFiles}
                      />
                    )}
                  </FormField>
                </CardBody>
              </Card>

              <Card className='ta-section'>
                <CardBody>
                  <div className='d-flex flex-wrap gap-2 align-items-center mb-3'>
                    <h6 className='ta-section__title mb-0 flex-grow-1'>
                      <i className='ri-links-line me-2' />
                      Action Link
                    </h6>
                    <ActionButton
                      variant='secondary'
                      size='sm'
                      icon='ri-add-line'
                      onClick={addActionLink}
                    >
                      Add Redirect Link
                    </ActionButton>
                  </div>

                  {formData.actionLinks.length === 0 && (
                    <div className='ta-empty'>No redirect links added yet.</div>
                  )}

                  {formData.actionLinks.map((link, index) => (
                    <div key={index} className='ta-action-link'>
                      <div className='d-flex align-items-center mb-2'>
                        <span className='fw-semibold flex-grow-1'>
                          Link {index + 1}
                        </span>
                        <ActionButton
                          variant='icon'
                          danger
                          icon='ri-delete-bin-line'
                          label='Remove'
                          confirm={{
                            title: 'Remove redirect link?',
                            message:
                              'This redirect link will be removed from the announcement.',
                            confirmLabel: 'Remove link',
                          }}
                          onClick={() => removeActionLink(index)}
                        />
                      </div>
                      <FormGrid columns={2}>
                        <TextField
                          span='full'
                          label='Description'
                          name={actionLinkFieldName(index, 'description')}
                          required
                          placeholder='e.g. Register as a participant'
                          value={link.description}
                          error={
                            errors[actionLinkFieldName(index, 'description')]
                          }
                          onChange={e =>
                            updateActionLink(
                              index,
                              'description',
                              e.target.value
                            )
                          }
                        />
                        <TextField
                          label='Button label'
                          name={actionLinkFieldName(index, 'buttonLabel')}
                          required
                          placeholder='e.g. Register now'
                          value={link.buttonLabel}
                          error={
                            errors[actionLinkFieldName(index, 'buttonLabel')]
                          }
                          onChange={e =>
                            updateActionLink(
                              index,
                              'buttonLabel',
                              e.target.value
                            )
                          }
                        />
                        <TextField
                          label='Target URL'
                          name={actionLinkFieldName(index, 'targetUrl')}
                          required
                          placeholder='https://example.com'
                          hint='Must start with http:// or https://'
                          value={link.targetUrl}
                          error={
                            errors[actionLinkFieldName(index, 'targetUrl')]
                          }
                          onChange={e =>
                            updateActionLink(index, 'targetUrl', e.target.value)
                          }
                        />
                      </FormGrid>
                    </div>
                  ))}
                </CardBody>
              </Card>

              <Card className='ta-section'>
                <CardBody>
                  <h6 className='ta-section__title'>
                    <i className='ri-global-line me-2' />
                    Publication Setting
                  </h6>

                  {isDraftStatus && (
                    <p className='text-muted small'>
                      Publication is disabled while the tender status is Draft —
                      the announcement would not appear on the public page.
                    </p>
                  )}

                  <Row className='g-3'>
                    {renderPublishCard(
                      PUBLISH_MODE.UNPUBLISH,
                      'ri-global-line',
                      'Unpublished',
                      'Save as a private draft. Hidden from the public announcement page.',
                      false
                    )}
                    {renderPublishCard(
                      PUBLISH_MODE.PUBLISH_NOW,
                      'ri-global-line',
                      middleCardLabel,
                      middleCardDesc,
                      isDraftStatus
                    )}
                    {renderPublishCard(
                      PUBLISH_MODE.SCHEDULE,
                      'ri-calendar-schedule-line',
                      isClosedStatus || isDraftStatus
                        ? 'Schedule Disabled'
                        : 'Schedule Publication',
                      isClosedStatus
                        ? 'Cannot schedule publication for closed tenders.'
                        : isDraftStatus
                          ? 'Cannot schedule publication while status is Draft.'
                          : 'Set a specific future date and time for the announcement to automatically go live.',
                      isClosedStatus || isDraftStatus
                    )}
                  </Row>

                  {selectedPublishCard === PUBLISH_MODE.SCHEDULE &&
                    !isClosedStatus &&
                    !isDraftStatus && (
                      <div className='ta-schedule-box mt-3'>
                        <h6 className='fw-semibold mb-3'>
                          Scheduled date and time
                        </h6>
                        <FormGrid columns={2}>
                          <DateField
                            label='Scheduled date'
                            name='scheduledPublishDate'
                            required
                            value={formData.scheduledPublishDate}
                            error={errors.scheduledPublishDate}
                            onChange={date =>
                              setField('scheduledPublishDate', dateToYmd(date))
                            }
                          />
                          <DateField
                            label='Scheduled time (WIB)'
                            name='scheduledPublishTime'
                            picker='time'
                            value={hmToDate(formData.scheduledPublishTime)}
                            onChange={date =>
                              setField('scheduledPublishTime', dateToHm(date))
                            }
                          />
                        </FormGrid>
                      </div>
                    )}
                </CardBody>
              </Card>
            </ModalBody>
            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => setModalForm(false)}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-database-2-line'
                loading={saving}
                onClick={handleSaveForm}
              >
                {formData.id ? 'Save Changes' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TenderAnnouncementView;
